import { Add, Search } from "@mui/icons-material";
import { Box, InputAdornment, Typography } from "@mui/material";
import { getUserRoleDescription, getUserRolesLabel } from "components/MemberList/userRoles";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { ROLES } from "containers/ScopeContext/roles.enum";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAvailableActions, fetchAvailableSubjects, fetchRoles } from "store/permissionsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import CreateRoleModal from "./CreateRoleModal";
import EditRoleModal from "./EditRoleModal";
import RolesTable from "./RolesTable";
import type { Role } from "./types";

const RolesSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);

    const {
        roles: backendRoles,
        actionsFetchStatus,
        subjectsFetchStatus,
    } = useAppSelector((state) => state.permissions);

    useEffect(() => {
        dispatch(fetchRoles());

        // Fetch available actions and subjects from backend if not already loaded
        if (actionsFetchStatus === "idle") {
            dispatch(fetchAvailableActions());
        }
        if (subjectsFetchStatus === "idle") {
            dispatch(fetchAvailableSubjects());
        }
    }, [dispatch, actionsFetchStatus, subjectsFetchStatus]);

    const roles: Role[] = useMemo(() => {
        if (!Array.isArray(backendRoles)) {
            return [];
        }

        const rolesArray = backendRoles.map((roleData) => {
            const isSystem = !roleData.isCustom;
            // For system roles, use translated labels and descriptions
            const displayName = isSystem ? getUserRolesLabel(roleData.name as ROLES) ?? roleData.name : roleData.name;
            const displayDescription = isSystem
                ? roleData.description || getUserRoleDescription(roleData.name as ROLES) || ""
                : roleData.description || "";

            return {
                id: roleData.id?.toString() || roleData.name,
                name: displayName,
                originalName: roleData.name,
                description: displayDescription,
                brands: roleData.brands || [],
                contractTypes: roleData.contractTypes || [],
                userCount: roleData.userCount || 0,
                type: (roleData.isCustom ? "custom" : "system") as "system" | "custom",
            };
        });

        // Sort: custom roles first, then system roles
        return rolesArray.sort((a, b) => {
            if (a.type === "custom" && b.type !== "custom") return -1;
            if (a.type !== "custom" && b.type === "custom") return 1;
            return 0;
        });
    }, [backendRoles]);

    const filteredRoles = roles.filter(
        (role) =>
            role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            role.description.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const handleEditRole = (role: Role) => {
        setEditingRole(role);
    };

    const handleCloseEditModal = () => {
        setEditingRole(null);
    };

    return (
        <Box sx={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight={600} color="text.primary" gutterBottom>
                        {t("Roles.Title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("Roles.Subtitle")}
                    </Typography>
                </Box>
                <StyledButton
                    buttonType="primary"
                    onClick={() => setCreateModalOpen(true)}
                    startIcon={<Add />}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    {t("Roles.NewCustomRole")}
                </StyledButton>
            </Box>

            <Box mb={3}>
                <StyledTextField
                    fullWidth
                    placeholder={t("Roles.SearchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" sx={{ color: "text.secondary" }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <RolesTable roles={filteredRoles} onEditRole={handleEditRole} />

            <CreateRoleModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />

            <EditRoleModal open={!!editingRole} onClose={handleCloseEditModal} role={editingRole} />
        </Box>
    );
};

export default RolesSettings;
